import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Stack,
  Divider,
} from "@mui/material";
import {
  ArrowForward,
  Person,
  Payments,
} from "@mui/icons-material";
import WishlistButton from "./WishlistBtn";
import catalogFallback from '../../Images/catalog.png';

export default function Catalog(props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);

  const redirect = (id) => {
    navigate(`/db-shiksha/kaas/student/course/${id}`);
  };

  const shouldTruncate = props.coursetitle.length > 40;
  const displayTitle = shouldTruncate
    ? props.coursetitle.slice(0, 40) + "..."
    : props.coursetitle;

  return (
    <>
      <Card
        sx={{
          borderRadius: '24px',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          '&:hover': {
            transform: 'translateY(-10px)',
            boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
            borderColor: '#D4AF37',
          },
        }}
      >
        {/* Course Banner */}
        <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
          <CardMedia
            component="img"
            image={props.cover_img || catalogFallback}
            alt={props.coursetitle}
            sx={{ height: '100%', objectFit: 'cover' }}
          />
          <Box sx={{ 
            position: 'absolute', 
            top: 15, 
            right: 15, 
            zIndex: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            borderRadius: '12px',
            p: 0.5
          }}>
            <WishlistButton
              courseId={props.courseId}
              isWishlisted={props.isWishlisted}
              variant="text"
            />
          </Box>
        </Box>

        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack spacing={1} sx={{ mb: 2 }}>
            <Tooltip title={props.coursetitle} disableHoverListener={!shouldTruncate}>
              <Typography 
                variant="h6" 
                fontWeight={800} 
                lineHeight={1.3}
                sx={{ 
                  minHeight: '2.6em',
                  cursor: shouldTruncate ? 'help' : 'default'
                }}
              >
                {displayTitle}
                {shouldTruncate && (
                  <Typography 
                    component="span" 
                    color="primary" 
                    sx={{ fontSize: '0.8rem', ml: 1, cursor: 'pointer', verticalAlign: 'middle' }}
                    onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
                  >
                    (More)
                  </Typography>
                )}
              </Typography>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Person sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                {props.instructor}
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 'auto' }}>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Payments sx={{ color: '#D4AF37' }} />
                <Typography variant="h6" fontWeight={900}>
                  ₹{props.price}
                </Typography>
              </Box>
              
              <Button 
                variant="contained" 
                endIcon={<ArrowForward />}
                onClick={() => redirect(props.courseId)}
                sx={{ 
                  borderRadius: '50px', 
                  fontWeight: 800,
                  px: 3,
                  boxShadow: '0 4px 15px rgba(212, 175, 55, 0.2)'
                }}
              >
                Explore
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Full Title Dialog */}
      <Dialog 
        open={showModal} 
        onClose={() => setShowModal(false)}
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Full Course Title</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{props.coursetitle}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)} sx={{ fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
